import {
  type CopyFlavours,
  type CopyResult,
  copyFlavours,
} from "./clipboard.js";

/**
 * A button that copies, and says what it did.
 *
 * The reporting is the part worth having a module for. A copy can half-succeed
 * — the plain text goes and the colours do not, on a browser without
 * `ClipboardItem` — and a button that says "Copied!" either way sends somebody
 * to paste into a document and find the colours missing without knowing why.
 */

/** How long the button holds its confirmation before going back to normal. */
const feedbackMilliseconds = 2000;

/** What the button says for each outcome. */
const messages: Readonly<Record<CopyResult, string>> = {
  copied: "Copied",
  "copied-without-colours": "Copied as plain text",
  failed: "Could not copy",
};

/**
 * Wire a button to copy whatever the page currently has.
 *
 * The flavours are produced on click rather than handed over up front, because
 * the output changes as the visitor types and a button holding a snapshot
 * would copy whatever was on screen when it was wired.
 *
 * Returning `undefined` from `produce` means there is nothing to copy, and the
 * button does nothing rather than putting an empty string on the clipboard.
 */
export function wireCopyButton(
  button: HTMLButtonElement,
  produce: () => CopyFlavours | undefined,
): void {
  // `textContent` is "" rather than null for a button with no label, so this
  // cannot be a `??`: an empty button would keep confirming and then go blank.
  const label = button.textContent.trim();
  const original = label === "" ? "Copy" : label;
  let restore: ReturnType<typeof setTimeout> | undefined;

  button.addEventListener("click", () => {
    const flavours = produce();
    if (flavours === undefined) {
      return;
    }

    void copyFlavours(flavours).then((result) => {
      button.textContent = messages[result];
      button.classList.toggle("cb-copy-failed", result === "failed");

      if (restore !== undefined) {
        clearTimeout(restore);
      }
      restore = setTimeout(() => {
        button.textContent = original;
        button.classList.remove("cb-copy-failed");
        restore = undefined;
      }, feedbackMilliseconds);
    });
  });
}
